/**
 * Pragmatic Intelligence Settings
 *
 * 4-tab settings page: Stream Mode, Signal Sensitivity,
 * Accommodations, Calibration. Sticky save footer with change tracking.
 */

import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuthStore } from '@personic/shared'
import type {
  IdentityMode,
  PragmaticUserSettings,
  CalibrationWeights,
} from '@personic/shared'
import {
  usePragmaticSettings,
  useUpdatePragmaticSettings,
  useUpdateCalibration,
  useAvailableAccommodations,
  useSetAccommodations,
} from '@/hooks/usePragmatic'
import IdentityModeSelector from '@/components/engage/IdentityModeSelector'
import CulturalContextSelector from '@/components/engage/CulturalContextSelector'
import NeurodivergentSelector from '@/components/engage/NeurodivergentSelector'

type TabId = 'stream-mode' | 'sensitivity' | 'accommodations' | 'calibration'

const tabs: Array<{ id: TabId; label: string; icon: string }> = [
  { id: 'stream-mode', label: 'Stream Mode', icon: '🔄' },
  { id: 'sensitivity', label: 'Signal Sensitivity', icon: '📊' },
  { id: 'accommodations', label: 'Accommodations', icon: '🌍' },
  { id: 'calibration', label: 'Calibration', icon: '⚖️' },
]

// =============================================================================
// Signal sensitivity definitions
// =============================================================================

interface SignalDef {
  key: string
  label: string
  description: string
}

const signalCategories: Array<{
  id: string
  label: string
  icon: string
  signals: SignalDef[]
}> = [
  {
    id: 'certainty',
    label: 'Certainty Signals',
    icon: '🎯',
    signals: [
      { key: 'certainty', label: 'Certainty Detection', description: 'Detect confidence and certainty levels in language' },
      { key: 'boosters', label: 'Booster Tracking', description: 'Track certainty boosters (definitely, absolutely, clearly)' },
    ],
  },
  {
    id: 'hedging',
    label: 'Hedging Signals',
    icon: '🌊',
    signals: [
      { key: 'hedging', label: 'Hedge Detection', description: 'Detect hedging and uncertainty markers' },
      { key: 'epistemic', label: 'Epistemic Analysis', description: 'Full epistemic marker analysis (hedges + boosters)' },
    ],
  },
  {
    id: 'commitment',
    label: 'Commitment Signals',
    icon: '🤝',
    signals: [
      { key: 'commitment', label: 'Commitment Tracking', description: 'Track commitment strength and conditionality' },
      { key: 'fused', label: 'Fused Signal', description: 'Multi-modal signal fusion (semantic + acoustic)' },
    ],
  },
]

// =============================================================================
// LOCAL STATE TYPES
// =============================================================================

interface LocalSignalState {
  enabled: boolean
  sensitivity: number
}

interface LocalState {
  identityMode: IdentityMode
  signalToggles: Record<string, LocalSignalState>
  culturalContext: string | null
  ndProfiles: string[]
  calibration: CalibrationWeights
}

function buildLocalState(settings?: PragmaticUserSettings): LocalState {
  return {
    identityMode: settings?.identity_mode || 'self_focused',
    signalToggles: {
      certainty: { enabled: settings?.signal_toggles?.epistemic !== false, sensitivity: 70 },
      boosters: { enabled: settings?.signal_toggles?.epistemic !== false, sensitivity: 70 },
      hedging: { enabled: settings?.signal_toggles?.epistemic !== false, sensitivity: 70 },
      epistemic: { enabled: settings?.signal_toggles?.epistemic !== false, sensitivity: 70 },
      commitment: { enabled: settings?.signal_toggles?.commitment !== false, sensitivity: 70 },
      fused: { enabled: settings?.signal_toggles?.fused !== false, sensitivity: 70 },
    },
    culturalContext: null,
    ndProfiles: [],
    calibration: {
      hedging_weight: settings?.calibration_weights?.hedging_weight ?? 1.0,
      certainty_weight: settings?.calibration_weights?.certainty_weight ?? 1.0,
      commitment_weight: settings?.calibration_weights?.commitment_weight ?? 1.0,
    },
  }
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function PragmaticSettings() {
  const [activeTab, setActiveTab] = useState<TabId>('stream-mode')
  const { user } = useAuthStore()
  const profileId = user?.id || ''

  // Data hooks
  const { data: settings, isLoading: settingsLoading } = usePragmaticSettings(profileId)
  const { data: accommodationsData, isLoading: accommodationsLoading } = useAvailableAccommodations()
  const updateSettings = useUpdatePragmaticSettings(profileId)
  const updateCalibration = useUpdateCalibration(profileId)
  const setAccommodations = useSetAccommodations(profileId)

  // Local state
  const [local, setLocal] = useState<LocalState>(buildLocalState())
  const [hasChanges, setHasChanges] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Initialize from server
  useEffect(() => {
    if (settings) {
      setLocal(buildLocalState(settings))
      setHasChanges(false)
    }
  }, [settings])

  // Change helpers
  const updateLocal = useCallback((updater: (prev: LocalState) => LocalState) => {
    setLocal((prev) => {
      const next = updater(prev)
      setHasChanges(true)
      return next
    })
  }, [])

  // Save
  const handleSave = async () => {
    try {
      // Update settings (mode + toggles + privacy)
      await updateSettings.mutateAsync({
        identity_mode: local.identityMode,
        signal_toggles: {
          epistemic: local.signalToggles.epistemic?.enabled,
          commitment: local.signalToggles.commitment?.enabled,
          fused: local.signalToggles.fused?.enabled,
        },
      })

      // Update calibration weights
      await updateCalibration.mutateAsync(local.calibration)

      // Update accommodations
      await setAccommodations.mutateAsync({
        cultural_context: local.culturalContext || undefined,
        neurodivergent_profiles: local.ndProfiles,
      })

      setHasChanges(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 2000)
    } catch {
      // Errors handled by React Query
    }
  }

  const handleCancel = () => {
    if (settings) {
      setLocal(buildLocalState(settings))
      setHasChanges(false)
    }
  }

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-engage-primary/20 border-t-engage-primary rounded-full animate-spin" />
          <p className="mt-4 text-gray-500">Loading pragmatic settings...</p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl font-bold text-gray-900">Pragmatic Intelligence</h1>
        <p className="text-gray-600 mt-1">
          Configure dual-stream analysis, signal sensitivity, cultural accommodations, and calibration
        </p>
      </motion.div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-4 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-engage-primary text-engage-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
        {activeTab === 'stream-mode' && (
          <StreamModeTab
            identityMode={local.identityMode}
            onModeChange={(mode) => updateLocal((s) => ({ ...s, identityMode: mode }))}
          />
        )}
        {activeTab === 'sensitivity' && (
          <SensitivityTab
            signalToggles={local.signalToggles}
            onToggleChange={(key, enabled) =>
              updateLocal((s) => ({
                ...s,
                signalToggles: {
                  ...s.signalToggles,
                  [key]: { ...s.signalToggles[key], enabled },
                },
              }))
            }
            onSensitivityChange={(key, sensitivity) =>
              updateLocal((s) => ({
                ...s,
                signalToggles: {
                  ...s.signalToggles,
                  [key]: { ...s.signalToggles[key], sensitivity },
                },
              }))
            }
          />
        )}
        {activeTab === 'accommodations' && (
          <AccommodationsTab
            contexts={accommodationsData?.cultural_contexts || []}
            ndProfiles={accommodationsData?.neurodivergent_profiles || []}
            culturalContext={local.culturalContext}
            selectedNd={local.ndProfiles}
            onCulturalChange={(ctx) => updateLocal((s) => ({ ...s, culturalContext: ctx }))}
            onNdChange={(profiles) => updateLocal((s) => ({ ...s, ndProfiles: profiles }))}
            isLoading={accommodationsLoading}
          />
        )}
        {activeTab === 'calibration' && (
          <CalibrationTab
            weights={local.calibration}
            onWeightChange={(key, value) =>
              updateLocal((s) => ({
                ...s,
                calibration: { ...s.calibration, [key]: value },
              }))
            }
            onReset={() =>
              updateLocal((s) => ({
                ...s,
                calibration: { hedging_weight: 1.0, certainty_weight: 1.0, commitment_weight: 1.0 },
              }))
            }
          />
        )}
      </motion.div>

      {/* Sticky Save Footer */}
      <AnimatePresence>
        {hasChanges && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40"
          >
            <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-amber-500 rounded-full" />
                <span className="text-sm text-gray-600">You have unsaved changes</span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateSettings.isPending || updateCalibration.isPending}
                  className="px-6 py-2 bg-engage-primary text-white rounded-lg text-sm font-medium hover:bg-engage-primary/90 transition-colors disabled:opacity-50"
                >
                  {updateSettings.isPending || updateCalibration.isPending
                    ? 'Saving...'
                    : 'Save Changes'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save Success Toast */}
      <AnimatePresence>
        {saveSuccess && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm font-medium">Settings saved successfully</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// =============================================================================
// TAB COMPONENTS
// =============================================================================

function StreamModeTab({
  identityMode,
  onModeChange,
}: {
  identityMode: IdentityMode
  onModeChange: (mode: IdentityMode) => void
}) {
  return (
    <div className="space-y-8">
      <IdentityModeSelector value={identityMode} onChange={onModeChange} />

      {/* Mode Explainer */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">How Stream Modes Work</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 text-sm mb-1">Self-Directed</h4>
            <p className="text-xs text-blue-700 leading-relaxed">
              Analyzes the API caller's own signals. Ideal for agent training and coaching.
              No consent required from the other party.
            </p>
          </div>
          <div className="p-4 bg-amber-50 rounded-lg">
            <h4 className="font-medium text-amber-900 text-sm mb-1">Counterparty</h4>
            <p className="text-xs text-amber-700 leading-relaxed">
              Analyzes the other party's signals to understand their intent and emotions.
              Standard Engage use case for customer intelligence.
            </p>
          </div>
          <div className="p-4 bg-emerald-50 rounded-lg">
            <h4 className="font-medium text-emerald-900 text-sm mb-1">Dual-Stream</h4>
            <p className="text-xs text-emerald-700 leading-relaxed">
              Analyzes both parties with cross-stream correlation. Shows how each
              party influences the other's communication patterns.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function SensitivityTab({
  signalToggles,
  onToggleChange,
  onSensitivityChange,
}: {
  signalToggles: Record<string, LocalSignalState>
  onToggleChange: (key: string, enabled: boolean) => void
  onSensitivityChange: (key: string, sensitivity: number) => void
}) {
  const [openCategories, setOpenCategories] = useState<Set<string>>(
    new Set(signalCategories.map((c) => c.id))
  )

  const toggleCategory = (id: string) => {
    setOpenCategories((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Enable or disable signal categories and adjust sensitivity thresholds.
      </p>

      {signalCategories.map((category) => {
        const isOpen = openCategories.has(category.id)
        return (
          <div key={category.id} className="card overflow-hidden">
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(category.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{category.icon}</span>
                <h3 className="font-semibold text-gray-900">{category.label}</h3>
              </div>
              <motion.svg
                animate={{ rotate: isOpen ? 180 : 0 }}
                className="w-5 h-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </motion.svg>
            </button>

            {/* Category Content */}
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4 space-y-4">
                    {category.signals.map((signal) => {
                      const state = signalToggles[signal.key] || { enabled: true, sensitivity: 70 }
                      return (
                        <div key={signal.key} className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={state.enabled}
                                  onChange={(e) => onToggleChange(signal.key, e.target.checked)}
                                  className="sr-only peer"
                                />
                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-engage-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-engage-primary" />
                              </label>
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{signal.label}</h4>
                                <p className="text-xs text-gray-500">{signal.description}</p>
                              </div>
                            </div>

                            {state.enabled && (
                              <div className="mt-3 ml-12">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs text-gray-500">Sensitivity</span>
                                  <span className="text-xs font-mono text-gray-600">
                                    {state.sensitivity}%
                                  </span>
                                </div>
                                <input
                                  type="range"
                                  min={0}
                                  max={100}
                                  value={state.sensitivity}
                                  onChange={(e) => onSensitivityChange(signal.key, Number(e.target.value))}
                                  className="w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-engage-primary"
                                />
                                <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                                  <span>Low</span>
                                  <span>High</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}

function AccommodationsTab({
  contexts,
  ndProfiles,
  culturalContext,
  selectedNd,
  onCulturalChange,
  onNdChange,
  isLoading,
}: {
  contexts: Array<{ name: string; description: string; region: string; adjustments: Record<string, string> }>
  ndProfiles: Array<{ name: string; description: string; accommodations: Record<string, string> }>
  culturalContext: string | null
  selectedNd: string[]
  onCulturalChange: (ctx: string | null) => void
  onNdChange: (profiles: string[]) => void
  isLoading: boolean
}) {
  return (
    <div className="space-y-8">
      <CulturalContextSelector
        contexts={contexts}
        value={culturalContext}
        onChange={onCulturalChange}
        isLoading={isLoading}
      />

      {/* Selected Context Summary */}
      {culturalContext && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-blue-50 rounded-lg"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-blue-800">Active Adjustments</span>
          </div>
          <p className="text-xs text-blue-700">
            Baseline expectations adjusted for <strong>{culturalContext.replace(/_/g, ' ')}</strong> context.
            Signals will be interpreted relative to cultural communication norms.
          </p>
        </motion.div>
      )}

      <div className="border-t border-gray-200 pt-8">
        <NeurodivergentSelector
          profiles={ndProfiles}
          value={selectedNd}
          onChange={onNdChange}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}

function CalibrationTab({
  weights,
  onWeightChange,
  onReset,
}: {
  weights: CalibrationWeights
  onWeightChange: (key: keyof CalibrationWeights, value: number) => void
  onReset: () => void
}) {
  const sliders: Array<{
    key: keyof CalibrationWeights
    label: string
    description: string
    icon: string
  }> = [
    {
      key: 'hedging_weight',
      label: 'Hedging Weight',
      description: 'How much weight to give hedging signals in overall analysis',
      icon: '🌊',
    },
    {
      key: 'certainty_weight',
      label: 'Certainty Weight',
      description: 'How much weight to give certainty signals in overall analysis',
      icon: '🎯',
    },
    {
      key: 'commitment_weight',
      label: 'Commitment Weight',
      description: 'How much weight to give commitment signals in overall analysis',
      icon: '🤝',
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Adjust the relative weight of each signal type. Default is 1.0x for all.
        </p>
        <button
          onClick={onReset}
          className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Reset to Defaults
        </button>
      </div>

      <div className="space-y-6">
        {sliders.map((slider) => {
          const value = weights[slider.key] ?? 1.0
          return (
            <div key={slider.key} className="card p-5">
              <div className="flex items-start gap-4">
                <span className="text-2xl">{slider.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-medium text-gray-900">{slider.label}</h4>
                    <span className="text-sm font-mono font-semibold text-engage-primary">
                      {value.toFixed(1)}x
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">{slider.description}</p>
                  <input
                    type="range"
                    min={0}
                    max={2}
                    step={0.1}
                    value={value}
                    onChange={(e) => onWeightChange(slider.key, Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-engage-primary"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>0.0x (off)</span>
                    <span>1.0x (default)</span>
                    <span>2.0x (max)</span>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
